export const getAuthToken = (): string | null =>
  localStorage.getItem("authToken");

// Tenancy (Guides/TENANCY-DESIGN.md §5.1, CM-Central backend repo): the
// current logout previously cleared only authToken/mustChangePassword,
// leaving Redux-persisted AuthState/permissionsState and userProfile/userid
// behind in localStorage. Under single-tenant that's cosmetic; once
// multi-company users exist (a company switch re-mints the token), any
// leftover cache from the previous company is a real leak in the UI layer,
// not just stale data. Clear everything session-scoped here so every logout
// call site gets the fix for free — this is the single shared primitive both
// the header's logout links and the 401-interceptor redirect call.
export const clearAuthToken = (): void => {
  const keysToClear = [
    "authToken",
    "mustChangePassword",
    "userid",
    "userProfile",
    "AuthState",        // authSlice persisted state
    "permissionsState",  // permissionsSlice persisted state
  ];

  for (const key of keysToClear) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  // The axios response interceptor caches GET responses under "cache:<url>" keys
  // (see axiosInstance.tsx) for any request sent with X-Cache-Response: true.
  // Those are response BODIES — tenant-owned rows — and they were surviving
  // logout, because the fixed list above cannot name keys whose URL suffix is
  // only known at runtime. Sweep them by prefix instead.
  //
  // Tenancy (Guides/TENANCY-DESIGN.md §5.1): mandatory before any company
  // switcher ships, since a switch re-mints the token without a page reload and
  // the previous company's cached lists would otherwise still be served. Even
  // without a switcher, leaving one user's cached data for the next user of the
  // same browser is a leak in the UI layer.
  for (const storage of [localStorage, sessionStorage]) {
    const cacheKeys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith("cache:")) cacheKeys.push(key);
    }
    // Collected first, then removed — mutating while indexing skips entries.
    for (const key of cacheKeys) storage.removeItem(key);
  }
  // rememberedEmail / rememberMe deliberately NOT cleared — those persist
  // across logout by design (pre-fill the login form), not session state.
  // deviceToken is likewise deliberately NOT cleared: "trust this device for
  // 30 days" is a per-device credential, not session state — clearing it on
  // every logout defeated the whole feature (OTP was asked again immediately
  // after the very next login). It expires server-side on its own schedule
  // (see UserTrustedDevice.ExpiryDate / AccountService.IsDeviceTrustedAsync).
};

export const getStoredItem = (key: string): string | null =>
  localStorage.getItem(key);
